import { SxProps } from '@mui/material';
import {
  FieldTemplate,
  FieldType,
  OptionSourceType,
  SelectFieldDisplayComponent,
} from 'api/models/FieldTemplate';
import { FieldImportMeta } from 'api/models/Form';
import { LayoutItemStyle } from 'api/models/FormTemplate';
import { FormikValues } from 'formik';
import { useApplyFieldDefaultValue } from 'hooks/useApplyFieldDefaultValue';
import useEvaluateConditions from 'hooks/useEvaluateConditions';
import {
  DateField,
  NumberField,
  SelectField,
  StringField,
} from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields';
import CalculatedField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/CalculatedField';
import OptionAutocompleteField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/OptionAutocompleteField';
import RadioField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/RadioField';
import { ReportField } from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/ReportField';

interface FormFieldRendererProps {
  field: FieldTemplate;
  formik: FormikValues;
  isReadOnly?: boolean;
  sx?: SxProps;
  valueId?: string;
  externalFieldValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
  importedFieldMap?: Map<string, FieldImportMeta>;
  style?: LayoutItemStyle
}

export function FormFieldRenderer({
  field,
  formik,
  isReadOnly = false,
  valueId = '',
  sx,
  externalFieldValues,
  importedFieldMap,
  style
}: FormFieldRendererProps): JSX.Element {
  const isVisible = useEvaluateConditions({
    values: { ...externalFieldValues, ...formik.values },
    conditionGroup: field.isVisible,
  });

  useApplyFieldDefaultValue(formik, valueId, isVisible, field.fieldAttributes.defaultValue, field.fieldAttributes.isMultiSelect);
  
  if (!isVisible) {
    if (formik.values[valueId]) {
      formik.handleChange({ target: { name: valueId, value: null } });
    }

    return <></>;
  }

  switch (field.fieldType) {
    case FieldType.Number:
      return (
        <NumberField
          inputProps={{
            readOnly: isReadOnly,
          }}
          field={field}
          value={formik.values[valueId]}
          name={valueId}
          onChange={e => {
            const val = e.target.value;
            if (val === '') {
              return formik.setFieldValue(valueId, null);
            }
            return formik.setFieldValue(valueId, val);
          }}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          isImported={importedFieldMap?.has(field.id) ? true : false}
          sx={sx}
        />
      );
    case FieldType.Select:
      if (
        field.fieldAttributes.optionsSourceType === OptionSourceType.CodeLibrary
      ) {
        return (
          <OptionAutocompleteField
            inputProps={{
              readOnly: isReadOnly,
            }}
            onChange={(value: string | string[]) =>
              formik.setFieldValue(valueId, value)
            }
            onBlur={formik.handleBlur}
            value={formik.values[valueId]}
            defaultValue={
              field.fieldAttributes.defaultValue as string | string[] | null
            }
            isMultiSelect={field.fieldAttributes.isMultiSelect ?? false}
            label={field.fieldAttributes.label}
            optionsSourceCodeLibraryId={
              field.fieldAttributes.optionsSourceCodeLibraryId
            }
            isRequired={field.fieldAttributes.isRequired ?? false}
            sx={sx}
            error={Boolean(formik.errors[field.id])}
            helperText={formik.errors[field.id]}
            description={field.fieldAttributes.description}
            isImported={importedFieldMap?.has(field.id) ? true : false}
          />
        );
      }

      return field.fieldAttributes.component ===
        SelectFieldDisplayComponent.Dropdown ? (
        <SelectField
          inputProps={{
            readOnly: isReadOnly,
          }}
          field={field}
          value={
            field.fieldAttributes.isMultiSelect
              ? formik.values[valueId]
              : formik.values[valueId]
          }
          name={valueId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          onClear={() => {
            formik.setFieldValue(
              field.id,
              field.fieldAttributes.isMultiSelect ? [] : null,
            );
          }}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          sx={sx}
          isImported={importedFieldMap?.has(field.id) ? true : false}
        />
      ) : (
        <RadioField
          inputProps={{
            readOnly: isReadOnly,
          }}
          field={field}
          value={formik.values[valueId]}
          name={valueId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          sx={sx}
          isImported={importedFieldMap?.has(field.id) ? true : false}
          style={style}
        />
      );
    case FieldType.Date:
      return (
        <DateField
          inputProps={{
            readOnly: isReadOnly,
          }}
          field={field}
          value={formik.values[valueId]}
          name={valueId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          sx={sx}
          isImported={importedFieldMap?.has(field.id) ? true : false}
        />
      );
    case FieldType.Calculated:
      return (
        <CalculatedField
          inputProps={{
            readOnly: isReadOnly,
          }}
          field={field}
          value={formik.values[valueId]}
          name={valueId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          sx={sx}
          isImported={importedFieldMap?.has(field.id) ? true : false}
        />
      );
    case FieldType.Text:
      return (
        <StringField
          inputProps={{
            readOnly: isReadOnly,
          }}
          field={field}
          value={formik.values[valueId]}
          onChange={e => {
            const val = e.target.value;
            if (val === '') {
              return formik.setFieldValue(valueId, null);
            }
            return formik.setFieldValue(valueId, val);
          }}
          name={valueId}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[valueId])}
          helperText={formik.errors[valueId]}
          sx={sx}
          isImported={importedFieldMap?.has(field.id) ? true : false}
        />
      );
    case FieldType.Report:
      return (
        <ReportField
          field={field}
          onChange={formik.handleChange}
          value={formik.values[valueId]}
          name={valueId}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          sx={sx}
        />
      );
    default:
      throw new Error(`Unsupported field type: ${field.fieldType}`);
  }
}
